/**
 * OIDC State Manager
 * 
 * This utility helps manage OIDC authentication state persistence across page loads
 * when using @workspace/identity-react. It provides a workaround for authentication
 * state not being automatically restored after redirects.
 * 
 * TODO: Remove this workaround when @workspace/identity-react properly handles state persistence
 */

interface OIDCStateSnapshot {
  isAuthenticated: boolean;
  userEmail?: string;
  userId?: string;
  organisationId?: string;
  accessToken?: string;
  timestamp: number;
}

const OIDC_STATE_KEY = 'oidc_state_snapshot';
const STATE_VALIDITY_MS = 30000; // 30 seconds

/**
 * Save the current OIDC authentication state to sessionStorage
 */
export const saveOIDCState = async (identity: {
  isAuthenticated: boolean;
  user?: {
    email?: string;
    id?: string;
    organisationId?: string;
  };
  getAccessToken?: () => Promise<string>;
}): Promise<void> => {
  if (identity.isAuthenticated && identity.user) {
    let accessToken: string | undefined;
    
    // Try to get the access token if the function is provided
    if (identity.getAccessToken) {
      try {
        accessToken = await identity.getAccessToken();
      } catch (error) {
        console.warn('OIDC State: Failed to get access token for saving:', error);
      }
    }
    
    const snapshot: OIDCStateSnapshot = {
      isAuthenticated: identity.isAuthenticated,
      userEmail: identity.user.email,
      userId: identity.user.id,
      organisationId: identity.user.organisationId,
      accessToken,
      timestamp: Date.now()
    };
    
    sessionStorage.setItem(OIDC_STATE_KEY, JSON.stringify(snapshot));
  }
};

/**
 * Load the OIDC authentication state from sessionStorage
 */
export const loadOIDCState = (): OIDCStateSnapshot | null => {
  try {
    const stored = sessionStorage.getItem(OIDC_STATE_KEY);
    if (!stored) return null;
    
    const snapshot: OIDCStateSnapshot = JSON.parse(stored);
    const age = Date.now() - snapshot.timestamp;
    
    // Check if the stored state is still valid (not too old)
    if (age > STATE_VALIDITY_MS) {
      clearOIDCState();
      return null;
    }
    
    return snapshot;
  } catch (error) {
    console.warn('Failed to load OIDC state:', error);
    clearOIDCState();
    return null;
  }
};

/**
 * Clear the stored OIDC state
 */
export const clearOIDCState = (): void => {
  sessionStorage.removeItem(OIDC_STATE_KEY);
};

/**
 * Check if we have a valid stored authentication state
 */
export const hasValidOIDCState = (): boolean => {
  const state = loadOIDCState();
  return state?.isAuthenticated === true;
};
